import React, { Component, Children } from 'react';
import PropTypes from 'prop-types';
import { CloseSUI, CloseXsUI } from '@hig/icons';
import ThemeContext from '@hig/theme-context';
import { ControlBehavior } from '@hig/behaviors';
import { cx, css } from 'emotion';
import Typography from '@hig/typography';
import { createCustomClassNames, createButtonEventHandlers } from '@hig/utils';
import { polyfill } from 'react-lifecycles-compat';
import memoize from 'lodash.memoize';

var alignments = Object.freeze({
  LEFT: "left",
  CENTER: "center",
  RIGHT: "right"
});

var variants = Object.freeze({
  BOX: "box",
  CANVAS: "canvas",
  UNDERLINE: "underline"
});

var orientations = Object.freeze({
  HORIZONTAL: "horizontal",
  VERTICAL: "vertical"
});

var AVAILABLE_ALIGNMENTS = Object.freeze(Object.values(alignments));
var AVAILABLE_VARIANTS = Object.freeze(Object.values(variants));
var AVAILABLE_ORIENTATIONS = Object.freeze(Object.values(orientations));

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function getHaloStyles(_ref, themeData) {
  var active = _ref.active,
      hasHover = _ref.hasHover,
      hasFocus = _ref.hasFocus,
      isPressed = _ref.isPressed,
      variant = _ref.variant,
      orientation = _ref.orientation,
      disabled = _ref.disabled;

  var styles = {
    position: "absolute",
    left: 0,
    height: 0,
    width: 0,
    transitionDuration: "0.3s",
    transitionProperty: "height, width"
  };

  if (variant && variant === variants.UNDERLINE) {
    styles = _extends({}, styles, {
      bottom: 0,
      width: "100%",
      backgroundColor: themeData["tabs.underline.hover.haloColor"],
      overflow: "visible",
      "&:after": {
        position: "absolute",
        top: "100%",
        left: 0,
        backgroundColor: themeData["tabs.focus.haloColor"],
        content: "\" \"",
        width: "100%",
        height: !disabled && hasFocus ? themeData["tabs.focus.haloWidth"] : 0,
        transitionDuration: "0.3s",
        transitionProperty: "height"
      }
    });

    if (!disabled && (hasHover || active || isPressed)) {
      styles = _extends({}, styles, {
        height: themeData["tabs.focus.haloWidth"]
      }, (active || isPressed) && {
        backgroundColor: themeData["tabs.underline.active.haloColor"]
      });
    }
  } else if (variant && variant === variants.BOX) {
    styles = _extends({}, styles, {
      top: 0,
      backgroundColor: themeData["tabs.focus.haloColor"]
    });

    if (orientation && orientation === orientations.VERTICAL) {
      styles.height = "100%";
      if (!disabled && hasFocus) {
        styles.width = themeData["tabs.focus.haloWidth"];
      }
    } else {
      styles.width = "100%";
      if (!disabled && hasFocus) {
        styles.height = themeData["tabs.focus.haloWidth"];
      }
    }
  }

  return styles;
}

function getTabBackground(_ref2, themeData) {
  var active = _ref2.active,
      hasHover = _ref2.hasHover,
      isPressed = _ref2.isPressed,
      variant = _ref2.variant,
      disabled = _ref2.disabled;

  if (!disabled && variant && variant !== variants.UNDERLINE) {
    if (active || isPressed) {
      return themeData["tabs." + variant + ".tab.active.backgroundColor"];
    }
    if (hasHover) {
      return themeData["tabs." + variant + ".tab.hover.backgroundColor"];
    }
  }
  return "transparent";
}

function getContentWrapperWidth(_ref3, themeData) {
  var label = _ref3.label,
      icon = _ref3.icon,
      closable = _ref3.closable;

  if (label) return "auto";
  if (icon && closable) {
    return "calc(" + themeData["tabs.iconSize"] + " + " + themeData["tabs.iconGutter"] + " + " + themeData["tabs.closeButton.minSize"] + ")";
  }
  if (icon) return themeData["tabs.iconSize"];

  return "0";
}

function getIconColor(_ref4, themeData) {
  var hasHover = _ref4.hasHover,
      isPressed = _ref4.isPressed;

  if (isPressed) return themeData["tabs.closeButton.pressed.iconColor"];
  if (hasHover) return themeData["tabs.closeButton.hover.iconColor"];
  return themeData["tabs.closeButton.default.iconColor"];
}

function stylesheet(props, themeData) {
  var active = props.active,
      hasHover = props.hasHover,
      isPressed = props.isPressed,
      label = props.label,
      icon = props.icon,
      variant = props.variant,
      orientation = props.orientation,
      disabled = props.disabled,
      closable = props.closable,
      customStylesheet = props.stylesheet;


  var styles = {
    tab: _extends({
      position: "relative",
      display: "flex",
      margin: "0"

    }, variant && variant === variants.UNDERLINE && {
      marginRight: themeData["tabs.underline.tab.gutter"],
      "&:last-of-type": {
        marginRight: "0"
      }
    }),
    buttonWrapper: _extends({
      position: "relative",
      display: "flex",
      width: orientation && orientation === orientations.VERTICAL ? "100%" : "auto",
      userSelect: "none",
      cursor: disabled ? "default" : "pointer",
      border: "0",
      transitionDuration: "0.3s",
      transitionProperty: "background-color",
      backgroundColor: getTabBackground(props, themeData),

      "&:focus": {
        outline: "none"
      }

    }, variant && variant === variants.UNDERLINE && {
      marginBottom: "-" + themeData["tabs.underline.wrapper.borderBottomWidth"],
      padding: "0 0 " + themeData["tabs.underline.tab.paddingBottom"] + " 0"
    }, variant && variant === variants.BOX && {
      padding: themeData["tabs.box.tab.paddingVertical"] + " " + themeData["tabs.box.tab.paddingHorizontal"]
    }, variant && variant === variants.CANVAS && {
      padding: themeData["tabs.canvas.tab.paddingVertical"] + " " + themeData["tabs.canvas.tab.paddingHorizontal"],
      transform: "skewX(-23deg)",
      transformOrigin: "0 100%"
    }),
    contentWrapper: {
      position: "relative",
      flexGrow: "1",
      transform: variant && variant === variants.CANVAS ? "skewX(23deg)" : "none",
      opacity: disabled ? themeData["colorScheme.opacity.disabled"] : "1",
      width: getContentWrapperWidth(props, themeData),
      display: orientation && orientation === orientations.VERTICAL ? "block" : "flex"
    },
    label: {
      display: "inline-block",
      position: "relative",
      background: "transparent",
      fontFamily: themeData["tabs.label.fontFamily"],
      fontSize: themeData["tabs.label.fontSize"],
      fontWeight: active ? themeData["tabs.label.active.fontWeight"] : themeData["tabs.label.inactive.fontWeight"],
      lineHeight: themeData["tabs.label.active.lineHeight"],
      textAlign: "center",
      paddingLeft: icon && variant && variant !== variants.UNDERLINE ? "calc(" + themeData["tabs.iconSize"] + " + " + themeData["tabs.iconGutter"] + ")" : "0",

      paddingRight: closable && variant && variant !== variants.UNDERLINE ? "calc(" + themeData["tabs.closeButton.minSize"] + " + " + themeData["tabs.closeButton.gutter"] + ")" : "0",

      color: themeData["tabs.label.fontColor"],

      // keep same amount of space whether it's active (bold font weight)
      // or not active (regular font weight)
      "&:before": {
        display: "block",
        content: "\"" + label + "\"",
        fontFamily: themeData["tabs.label.fontFamily"],
        fontSize: themeData["tabs.label.fontSize"],
        fontWeight: themeData["tabs.label.active.fontWeight"],
        height: "0",
        color: "transparent",
        overflow: "hidden",
        visibility: "hidden"
      }
    },
    halo: getHaloStyles(props, themeData),
    divider: _extends({}, variant && variant !== variants.UNDERLINE && {
      position: "absolute",
      top: "50%",
      right: "0",
      transform: "translateY(-50%)",
      height: themeData["tabs." + variant + ".dividerHeight"],
      width: themeData["tabs." + variant + ".dividerWidth"],
      backgroundColor: themeData["tabs." + variant + ".divider.borderColor"]
    }),
    icon: {
      display: "inline-block",
      position: "absolute",
      top: "50%",
      left: "0",
      transform: "translateY(-50%)",
      height: themeData["tabs.iconSize"],
      width: themeData["tabs.iconSize"],
      overflow: "hidden"
    },
    closeButton: {
      position: "absolute",
      top: "50%",
      right: "0",
      transform: "translateY(-50%)"
    },
    button: {
      boxShadow: "none",
      backgroundColor: "transparent",
      outline: "none",
      border: "none",
      padding: "0",
      margin: "0",
      width: themeData["tabs.closeButton.minSize"],
      height: themeData["tabs.closeButton.minSize"],
      cursor: "pointer",

      "& svg *": {
        fill: getIconColor({ hasHover: hasHover, isPressed: isPressed }, themeData),
        transitionDuration: "0.3s",
        transitionProperty: "fill"
      }
    },
    content: {
      flexGrow: 1,
      flexShrink: 1
    }
  };

  if (customStylesheet) {
    return customStylesheet(styles, props, themeData);
  }

  return styles;
}

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var TabCloseButtonPresenter = function (_React$Component) {
  _inherits(TabCloseButtonPresenter, _React$Component);

  function TabCloseButtonPresenter() {
    _classCallCheck(this, TabCloseButtonPresenter);

    return _possibleConstructorReturn(this, (TabCloseButtonPresenter.__proto__ || Object.getPrototypeOf(TabCloseButtonPresenter)).apply(this, arguments));
  }

  _createClass(TabCloseButtonPresenter, [{
    key: "render",
    value: function render() {
      var _props = this.props,
          disabled = _props.disabled,
          onBlur = _props.onBlur,
          onFocus = _props.onFocus,
          onMouseDown = _props.onMouseDown,
          onMouseEnter = _props.onMouseEnter,
          onMouseLeave = _props.onMouseLeave,
          onMouseUp = _props.onMouseUp,
          onClick = _props.onClick,
          customStylesheet = _props.stylesheet,
          otherProps = _objectWithoutProperties(_props, ["disabled", "onBlur", "onFocus", "onMouseDown", "onMouseEnter", "onMouseLeave", "onMouseUp", "onClick", "stylesheet"]);

      function handleClick(event) {
        event.preventDefault();
        event.stopPropagation();
        onClick(event);
      }

      return React.createElement(
        ControlBehavior,
        {
          onBlur: onBlur,
          onFocus: onFocus,
          onMouseDown: onMouseDown,
          onMouseEnter: onMouseEnter,
          onMouseLeave: onMouseLeave,
          onMouseUp: onMouseUp
        },
        function (_ref) {
          var hasFocus = _ref.hasFocus,
              hasHover = _ref.hasHover,
              isPressed = _ref.isPressed,
              handleBlur = _ref.onBlur,
              handleFocus = _ref.onFocus,
              handleMouseDown = _ref.onMouseDown,
              handleMouseEnter = _ref.onMouseEnter,
              handleMouseLeave = _ref.onMouseLeave,
              handleMouseUp = _ref.onMouseUp;
          return React.createElement(
            ThemeContext.Consumer,
            null,
            function (_ref2) {
              var resolvedRoles = _ref2.resolvedRoles,
                  metadata = _ref2.metadata;

              var styles = stylesheet({ hasFocus: hasFocus, hasHover: hasHover, isPressed: isPressed, stylesheet: customStylesheet }, resolvedRoles, metadata);
              var className = otherProps.className;

              var CloseIcon = metadata.densityId === "medium-density" ? CloseSUI : CloseXsUI;

              return React.createElement(
                "button",
                {
                  onBlur: handleBlur,
                  onFocus: handleFocus,
                  onMouseDown: handleMouseDown,
                  onMouseEnter: handleMouseEnter,
                  onMouseLeave: handleMouseLeave,
                  onMouseUp: handleMouseUp,
                  className: cx(css(styles.button), className),
                  tabIndex: "-1",
                  onClick: handleClick,
                  disabled: disabled,
                  title: "close"
                },
                React.createElement(CloseIcon, null)
              );
            }
          );
        }
      );
    }
  }]);

  return TabCloseButtonPresenter;
}(React.Component);

TabCloseButtonPresenter.propTypes = {
  /**
   * Prevents user actions on the button
   */
  disabled: PropTypes.bool,
  /**
   * Called when user moves focus away from the button
   */
  onBlur: PropTypes.func,
  /**
   * Called when user clicks the button
   */
  onClick: PropTypes.func,
  /**
   * Called when user moves focus onto the button
   */
  onFocus: PropTypes.func,
  /**
   * Called when mouse is pressed over the button
   */
  onMouseDown: PropTypes.func,
  /**
   * Called when mouse begins to move over the button
   */
  onMouseEnter: PropTypes.func,
  /**
   * Called when mouse stops moving over the button
   */
  onMouseLeave: PropTypes.func,
  /**
   * Called when mouse is released over the button
   */
  onMouseUp: PropTypes.func,
  /**
   * Function to modify the component's styles
   */
  stylesheet: PropTypes.func
};
TabCloseButtonPresenter.__docgenInfo = {
  "description": "",
  "displayName": "TabCloseButtonPresenter",
  "props": {
    "disabled": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Prevents user actions on the button"
    },
    "onBlur": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when user moves focus away from the button"
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when user clicks the button"
    },
    "onFocus": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when user moves focus onto the button"
    },
    "onMouseDown": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when mouse is pressed over the button"
    },
    "onMouseEnter": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when mouse begins to move over the button"
    },
    "onMouseLeave": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when mouse stops moving over the button"
    },
    "onMouseUp": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when mouse is released over the button"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function to modify the component's styles"
    }
  }
};

function _objectWithoutProperties$1(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function TabPresenter(_ref) {
  var active = _ref.active,
      hasFocus = _ref.hasFocus,
      hasHover = _ref.hasHover,
      isPressed = _ref.isPressed,
      label = _ref.label,
      icon = _ref.icon,
      disabled = _ref.disabled,
      closable = _ref.closable,
      variant = _ref.variant,
      orientation = _ref.orientation,
      showDivider = _ref.showDivider,
      onBlur = _ref.onBlur,
      onFocus = _ref.onFocus,
      onClick = _ref.onClick,
      onKeyDown = _ref.onKeyDown,
      onMouseEnter = _ref.onMouseEnter,
      onMouseLeave = _ref.onMouseLeave,
      onClose = _ref.onClose,
      customStylesheet = _ref.stylesheet,
      otherProps = _objectWithoutProperties$1(_ref, ["active", "hasFocus", "hasHover", "isPressed", "label", "icon", "disabled", "closable", "variant", "orientation", "showDivider", "onBlur", "onFocus", "onClick", "onKeyDown", "onMouseEnter", "onMouseLeave", "onClose", "stylesheet"]);

  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref2) {
      var resolvedRoles = _ref2.resolvedRoles,
          metadata = _ref2.metadata;

      var styles = stylesheet({
        active: active,
        hasFocus: hasFocus,
        hasHover: hasHover,
        isPressed: isPressed,
        label: label,
        icon: icon,
        variant: variant,
        orientation: orientation,
        disabled: disabled,
        closable: closable,
        stylesheet: customStylesheet
      }, resolvedRoles, metadata);

      var className = otherProps.className;


      var showIcon = variant !== variants.UNDERLINE && icon;
      var showClose = variant !== variants.UNDERLINE && closable;
      var showHalo = variant !== variants.CANVAS;

      var buttonClassName = createCustomClassNames(otherProps.className, "button");

      var iconClassName = createCustomClassNames(otherProps.className, "icon");

      var labelClassName = createCustomClassNames(otherProps.className, "label");

      var closeButtonClassName = createCustomClassNames(otherProps.className, "close-button");

      var haloClassName = createCustomClassNames(otherProps.className, "halo");

      var dividerClassName = createCustomClassNames(otherProps.className, "divider");

      return React.createElement(
        "li",
        { className: cx(css(styles.tab), className) },
        React.createElement(
          "div",
          {
            onBlur: onBlur,
            onFocus: onFocus,
            onClick: onClick,
            onKeyDown: onKeyDown,
            onMouseEnter: onMouseEnter,
            onMouseLeave: onMouseLeave,
            disabled: disabled,
            tabIndex: disabled ? "-1" : "0",
            role: "button",
            className: cx(css(styles.buttonWrapper), buttonClassName)
          },
          React.createElement(
            "div",
            { className: css(styles.contentWrapper) },
            showIcon && React.createElement(
              "span",
              { className: cx(css(styles.icon), iconClassName) },
              icon
            ),
            React.createElement(
              Typography,
              { className: cx(css(styles.label), labelClassName) },
              label
            ),
            showClose && React.createElement(TabCloseButtonPresenter, {
              className: cx(css(styles.closeButton), closeButtonClassName),
              onClick: onClose,
              stylesheet: customStylesheet
            })
          ),
          showHalo && React.createElement("div", { className: cx(css(styles.halo), haloClassName) }),
          showDivider && React.createElement("div", { className: cx(css(styles.divider), dividerClassName) })
        )
      );
    }
  );
}

TabPresenter.propTypes = {
  active: PropTypes.bool,
  label: PropTypes.string,
  icon: PropTypes.node,
  disabled: PropTypes.bool,
  closable: PropTypes.bool,
  variant: PropTypes.oneOf(AVAILABLE_VARIANTS),
  orientation: PropTypes.oneOf(AVAILABLE_ORIENTATIONS),
  showDivider: PropTypes.bool,
  hasFocus: PropTypes.bool,
  hasHover: PropTypes.bool,
  isPressed: PropTypes.bool,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onClose: PropTypes.func,
  stylesheet: PropTypes.func
};

TabPresenter.defaultProps = {
  variant: variants.UNDERLINE,
  label: "",
  orientation: orientations.HORIZONTAL
};
TabPresenter.__docgenInfo = {
  "description": "",
  "displayName": "TabPresenter",
  "props": {
    "active": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "label": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "",
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      }
    },
    "icon": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "disabled": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "closable": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "variant": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_VARIANTS"
      },
      "required": false,
      "description": "",
      "defaultValue": {
        "value": "variants.UNDERLINE",
        "computed": true
      }
    },
    "orientation": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_ORIENTATIONS"
      },
      "required": false,
      "description": "",
      "defaultValue": {
        "value": "orientations.HORIZONTAL",
        "computed": true
      }
    },
    "showDivider": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "hasFocus": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "hasHover": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "isPressed": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "onBlur": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onFocus": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onKeyDown": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseEnter": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseLeave": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onClose": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    }
  }
};

var _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _createClass$1 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties$2(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck$1(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$1(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$1(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var Tab = function (_Component) {
  _inherits$1(Tab, _Component);

  function Tab() {
    _classCallCheck$1(this, Tab);

    return _possibleConstructorReturn$1(this, (Tab.__proto__ || Object.getPrototypeOf(Tab)).apply(this, arguments));
  }

  _createClass$1(Tab, [{
    key: "render",
    value: function render() {
      var _props = this.props,
          active = _props.active,
          closable = _props.closable,
          disabled = _props.disabled,
          icon = _props.icon,
          label = _props.label,
          customStylesheet = _props.stylesheet,
          otherProps = _objectWithoutProperties$2(_props, ["active", "closable", "disabled", "icon", "label", "stylesheet"]);

      var className = otherProps.className;
      var variant = otherProps.variant,
          orientation = otherProps.orientation,
          showDivider = otherProps.showDivider,
          onMouseEnter = otherProps.onMouseEnter,
          onMouseLeave = otherProps.onMouseLeave,
          handleClick = otherProps.handleClick,
          handleKeyDown = otherProps.handleKeyDown,
          onClose = otherProps.onClose,
          render = otherProps.render;


      if (render) {
        return render(_extends$1({}, this.props));
      }

      return React.createElement(
        ControlBehavior,
        { onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave },
        function (_ref) {
          var hasFocus = _ref.hasFocus,
              hasHover = _ref.hasHover,
              isPressed = _ref.isPressed,
              onBlur = _ref.onBlur,
              onFocus = _ref.onFocus,
              handleMouseEnter = _ref.onMouseEnter,
              handleMouseLeave = _ref.onMouseLeave;
          return React.createElement(TabPresenter, {
            active: active,
            disabled: disabled,
            closable: closable,
            hasFocus: hasFocus,
            hasHover: hasHover,
            isPressed: isPressed,
            label: label,
            icon: icon,
            variant: variant,
            orientation: orientation,
            showDivider: showDivider,
            onBlur: onBlur,
            onFocus: onFocus,
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            onClick: handleClick,
            onKeyDown: handleKeyDown,
            onClose: onClose,
            className: className,
            stylesheet: customStylesheet
          });
        }
      );
    }
  }]);

  return Tab;
}(Component);

Tab.propTypes = {
  /**
   * DEPRECATED: Use the activeTabIndex or defaultActiveTabIndex prop on the Tabs
   * component instead.
   *
   * Specify if the tab is active
   * If more than one tabs are marked as active, the first one will take effect
   */
  active: PropTypes.bool,
  /**
   * Specify if the tab will have a close button
   * Only works when varient prop of parent Tabs is set to "box" or "canvas"
   */
  closable: PropTypes.bool,
  /**
   * Specify if the tab is disabled
   */
  disabled: PropTypes.bool,
  /**
   * A @hig/icon element
   * Icon will only be displayed when varient prop of parent Tabs is set to "box" or "canvas"
   */
  icon: PropTypes.node,
  /**
   * Sets the label of a tab
   */
  label: PropTypes.string,
  /**
   * Function to modify the component's styles
   */
  stylesheet: PropTypes.func
};
Tab.defaultProps = {
  active: false,
  disabled: false,
  closable: false
};
Tab.__docgenInfo = {
  "description": "",
  "displayName": "Tab",
  "props": {
    "active": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "DEPRECATED: Use the activeTabIndex or defaultActiveTabIndex prop on the Tabs\ncomponent instead.\n\nSpecify if the tab is active\nIf more than one tabs are marked as active, the first one will take effect",
      "defaultValue": {
        "value": "false",
        "computed": false
      }
    },
    "closable": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Specify if the tab will have a close button\nOnly works when varient prop of parent Tabs is set to \"box\" or \"canvas\"",
      "defaultValue": {
        "value": "false",
        "computed": false
      }
    },
    "disabled": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Specify if the tab is disabled",
      "defaultValue": {
        "value": "false",
        "computed": false
      }
    },
    "icon": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "A @hig/icon element\nIcon will only be displayed when varient prop of parent Tabs is set to \"box\" or \"canvas\""
    },
    "label": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Sets the label of a tab"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function to modify the component's styles"
    }
  }
};

var _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function getBackgroundColor(variant, themeData) {
  if (variant === variants.BOX) {
    return themeData["tabs.box.wrapper.backgroundColor"];
  }
  if (variant === variants.CANVAS) {
    return themeData["tabs.canvas.wrapper.backgroundColor"];
  }
  return "transparent";
}

function stylesheet$1(props, themeData) {
  var _justifyContent;

  var align = props.align,
      orientation = props.orientation,
      customStylesheet = props.stylesheet,
      variant = props.variant;


  var justifyContent = (_justifyContent = {}, _defineProperty(_justifyContent, alignments.LEFT, "flex-start"), _defineProperty(_justifyContent, alignments.CENTER, "center"), _defineProperty(_justifyContent, alignments.RIGHT, "flex-end"), _justifyContent);

  var styles = {
    wrapper: {
      display: "flex",
      flexWrap: "nowrap",
      justifyContent: "flex-start",
      alignItems: "stretch",
      flexDirection: orientation === orientations.HORIZONTAL ? "column" : "row"
    },
    tabsWrapper: _extends$2({
      boxSizing: "border-box",
      flexGrow: 0,
      flexShrink: 0,
      display: "flex",
      padding: 0,
      margin: 0,
      justifyContent: justifyContent[align],
      borderBottom: variant === variants.UNDERLINE ? themeData["tabs.underline.wrapper.borderBottomWidth"] + " solid " + themeData["tabs.underline.wrapper.borderBottomColor"] : 0,
      backgroundColor: getBackgroundColor(variant, themeData)

    }, orientation === orientations.VERTICAL && {
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "stretch"
    })
  };

  if (customStylesheet) {
    return customStylesheet(styles, props, themeData);
  }

  return styles;
}

function _objectWithoutProperties$3(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function TabsPresenter(_ref) {
  var align = _ref.align,
      children = _ref.children,
      orientation = _ref.orientation,
      customStylesheet = _ref.stylesheet,
      variant = _ref.variant,
      otherProps = _objectWithoutProperties$3(_ref, ["align", "children", "orientation", "stylesheet", "variant"]);

  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref2) {
      var resolvedRoles = _ref2.resolvedRoles;

      var styles = stylesheet$1({ align: align, orientation: orientation, stylesheet: customStylesheet, variant: variant }, resolvedRoles);

      var className = otherProps.className;

      var customClassName = createCustomClassNames(className, "tabs");

      return React.createElement(
        "ul",
        { className: cx(css(styles.tabsWrapper), customClassName) },
        children
      );
    }
  );
}

TabsPresenter.propTypes = {
  align: PropTypes.oneOf(AVAILABLE_ALIGNMENTS),
  variant: PropTypes.oneOf(AVAILABLE_VARIANTS),
  orientation: PropTypes.oneOf(AVAILABLE_ORIENTATIONS),
  children: PropTypes.node,
  stylesheet: PropTypes.func
};

TabsPresenter.defaultProps = {
  align: alignments.LEFT,
  variant: variants.UNDERLINE,
  orientation: orientations.HORIZONTAL
};
TabsPresenter.__docgenInfo = {
  "description": "",
  "displayName": "TabsPresenter",
  "props": {
    "align": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_ALIGNMENTS"
      },
      "required": false,
      "description": "",
      "defaultValue": {
        "value": "alignments.LEFT",
        "computed": true
      }
    },
    "variant": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_VARIANTS"
      },
      "required": false,
      "description": "",
      "defaultValue": {
        "value": "variants.UNDERLINE",
        "computed": true
      }
    },
    "orientation": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_ORIENTATIONS"
      },
      "required": false,
      "description": "",
      "defaultValue": {
        "value": "orientations.HORIZONTAL",
        "computed": true
      }
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    }
  }
};

function _objectWithoutProperties$4(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function ContentPresenter(_ref) {
  var children = _ref.children,
      customStylesheet = _ref.stylesheet,
      otherProps = _objectWithoutProperties$4(_ref, ["children", "stylesheet"]);

  var className = otherProps.className;

  var styles = stylesheet({ stylesheet: customStylesheet }, {});
  var customClassName = createCustomClassNames(className, "content");
  return React.createElement(
    "div",
    { className: cx(css(styles.content), customClassName) },
    children
  );
}

ContentPresenter.propTypes = {
  children: PropTypes.node,
  stylesheet: PropTypes.func
};
ContentPresenter.__docgenInfo = {
  "description": "",
  "displayName": "ContentPresenter",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    }
  }
};

var _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _createClass$2 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _classCallCheck$2(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$2(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$2(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var FIRST_TAB_INDEX = 0;
var DEFAULT_HOVERED_TAB_INDEX = -1;

/**
 * @typedef {Object} TabMeta
 * @property {string} key
 * @property {import("./tab").TabProps} props
 */

/**
 * @typedef {Object} TabsProps
 * @property {string} [align]
 * @property {string} [variant]
 * @property {string} [orientation]
 * @property {bool} [showTabDivider]
 * @property {number} [defaultActiveTabIndex]
 * @property {number} [activeTabIndex]
 * @property {function} [onTabChange]
 * @property {function} [onTabClose]
 * @property {ReactNode} [children]
 */

/**
 * @typedef {Object} TabsState
 * @property {number} activeTabIndex
 * @property {number} hoveredTabIndex
 * @property {string} effectiveAlign
 * @property {string} effectiveOrientation
 * @property {bool} effectiveShowTabDivider
 */

/**
 * @param {ReactNode} children
 * @returns {TabMeta[]}
 */
function createTabs(children) {
  return Children.toArray(children).reduce(function (result, child) {
    var type = child.type,
        key = child.key,
        _child$props = child.props,
        props = _child$props === undefined ? {} : _child$props;


    if (type === Tab) {
      result.push({ key: key, props: props });
    }

    return result;
  }, []);
}

/**
 * @param {TabsProps} tabsProps
 * @returns {number}
 */
function findInitialStateActiveTab(tabsProps) {
  if (tabsProps.defaultActiveTabIndex !== undefined) {
    return tabsProps.defaultActiveTabIndex;
  }

  var tabIndexWithActiveProp = createTabs(tabsProps.children).findIndex(function (_ref) {
    var props = _ref.props;
    return props.active;
  });
  if (tabIndexWithActiveProp >= 0) {
    return tabIndexWithActiveProp;
  }

  return FIRST_TAB_INDEX;
}

var Tabs = function (_Component) {
  _inherits$2(Tabs, _Component);

  function Tabs() {
    var _ref2;

    var _temp, _this, _ret;

    _classCallCheck$2(this, Tabs);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = _possibleConstructorReturn$2(this, (_ref2 = Tabs.__proto__ || Object.getPrototypeOf(Tabs)).call.apply(_ref2, [this].concat(args))), _this), _this.state = {
      /**
       * We maintain the active tab index in the state in case it was not
       * provided as a prop.
       */
      activeTabIndex: findInitialStateActiveTab(_this.props),
      hoveredTabIndex: DEFAULT_HOVERED_TAB_INDEX,
      effectiveAlign: alignments.LEFT,
      effectiveShowTabDivider: true,
      effectiveOrientation: orientations.HORIZONTAL
    }, _this.createTabEventHandlers = memoize(function (index, _ref3) {
      var disabled = _ref3.disabled;
      return _extends$3({}, createButtonEventHandlers(function () {
        return _this.onTabSelection(index, { disabled: disabled });
      }), {
        onMouseEnter: function onMouseEnter() {
          return _this.setHoveredTab(index, { disabled: disabled });
        },
        onMouseLeave: function onMouseLeave() {
          return _this.removeHoveredTab(index);
        },
        onClose: function onClose() {
          return _this.props.onTabClose(index);
        }
      });
    }), _this.renderTab = function (_ref4, index) {
      var key = _ref4.key,
          props = _ref4.props;
      var disabled = props.disabled,
          tabClassName = props.className;
      var _this$props = _this.props,
          variant = _this$props.variant,
          tabsClassName = _this$props.className;
      var _this$state = _this.state,
          hoveredTabIndex = _this$state.hoveredTabIndex,
          effectiveAlign = _this$state.effectiveAlign,
          effectiveOrientation = _this$state.effectiveOrientation,
          effectiveShowTabDivider = _this$state.effectiveShowTabDivider;

      var activeTabIndex = _this.getActiveTabIndex();

      var showTabDivider = effectiveShowTabDivider;
      if (index === activeTabIndex || index === activeTabIndex - 1) {
        showTabDivider = false;
      }
      if (index === hoveredTabIndex || index === hoveredTabIndex - 1) {
        showTabDivider = false;
      }

      var className = cx(tabClassName, createCustomClassNames(tabsClassName, "tab"));

      var payload = _extends$3({}, props, {
        key: key,
        variant: variant,
        className: className,
        showDivider: showTabDivider,
        align: effectiveAlign,
        orientation: effectiveOrientation,
        active: activeTabIndex === index
      }, _this.createTabEventHandlers(index, { disabled: disabled }));

      return React.createElement(Tab, payload);
    }, _temp), _possibleConstructorReturn$2(_this, _ret);
  }

  /** @type {TabsState} */


  _createClass$2(Tabs, [{
    key: "onTabSelection",


    /**
     * @param {number} nextActiveTabIndex
     * @param {TabMeta} tab
     */
    value: function onTabSelection(selectedTabIndex, _ref5) {
      var disabled = _ref5.disabled;

      this.props.onTabChange(selectedTabIndex);

      var prevActiveTabIndex = this.getActiveTabIndex();
      if (!disabled && prevActiveTabIndex !== selectedTabIndex) {
        this.setState({ activeTabIndex: selectedTabIndex });
      }
    }

    /**
     * If the activeTabIndex has been passed, use it. Otherwise, use our
     * internally managed activeTabIndex.
     * @returns {number}
     */

  }, {
    key: "getActiveTabIndex",
    value: function getActiveTabIndex() {
      return this.props.activeTabIndex !== undefined ? this.props.activeTabIndex : this.state.activeTabIndex;
    }

    /** @returns {TabMeta|undefined} */

  }, {
    key: "getActiveTab",
    value: function getActiveTab() {
      return this.getTabs()[this.getActiveTabIndex()];
    }

    /** @returns {TabMeta[]} */

  }, {
    key: "getTabs",
    value: function getTabs() {
      return createTabs(this.props.children);
    }

    /**
     * @param {number} nextHoveredTabIndex
     * @param {TabMeta} tab
     */

  }, {
    key: "setHoveredTab",
    value: function setHoveredTab(nextHoveredTabIndex, _ref6) {
      var disabled = _ref6.disabled;
      var prevHoveredTabIndex = this.state.hoveredTabIndex;

      if (disabled || prevHoveredTabIndex === nextHoveredTabIndex) return;
      this.setState({ hoveredTabIndex: nextHoveredTabIndex });
    }

    /**
     * @param {number} index
     */

  }, {
    key: "removeHoveredTab",
    value: function removeHoveredTab(index) {
      var hoveredTabIndex = this.state.hoveredTabIndex;

      if (hoveredTabIndex === index) {
        this.setState({ hoveredTabIndex: DEFAULT_HOVERED_TAB_INDEX });
      }
    }

    /**
     * @param {TabMeta} tab
     * @param {number} index
     * @returns {JSX.Element}
     */

  }, {
    key: "renderTabs",


    /**
     * @returns {JSX.Element}
     */
    value: function renderTabs() {
      var _props = this.props,
          className = _props.className,
          variant = _props.variant,
          customStylesheet = _props.stylesheet;
      var _state = this.state,
          effectiveAlign = _state.effectiveAlign,
          effectiveOrientation = _state.effectiveOrientation;

      return React.createElement(
        TabsPresenter,
        {
          variant: variant,
          align: effectiveAlign,
          orientation: effectiveOrientation,
          className: className,
          stylesheet: customStylesheet
        },
        this.getTabs().map(this.renderTab)
      );
    }

    /**
     * @returns {JSX.Element}
     */

  }, {
    key: "renderContent",
    value: function renderContent() {
      var _props2 = this.props,
          className = _props2.className,
          customStylesheet = _props2.stylesheet;

      var activeTab = this.getActiveTab();

      return React.createElement(
        ContentPresenter,
        { className: className, stylesheet: customStylesheet },
        activeTab ? activeTab.props.children : null
      );
    }
  }, {
    key: "render",
    value: function render() {
      var effectiveOrientation = this.state.effectiveOrientation;

      var styles = stylesheet$1({ orientation: effectiveOrientation });

      return React.createElement(
        "div",
        { className: cx(css(styles.wrapper), this.props.className) },
        this.renderTabs(),
        this.renderContent()
      );
    }
  }], [{
    key: "getDerivedStateFromProps",


    /**
     * @param {TabsProps} nextProps
     * @param {TabsState} prevState
     * @returns {TabsState | null}
     */
    value: function getDerivedStateFromProps(nextProps, prevState) {
      var children = nextProps.children,
          align = nextProps.align,
          variant = nextProps.variant,
          orientation = nextProps.orientation,
          showTabDivider = nextProps.showTabDivider;
      var prevActiveTabIndex = prevState.activeTabIndex,
          prevEffectiveAlign = prevState.effectiveAlign,
          prevEffectiveOrientation = prevState.effectiveOrientation,
          prevEffectiveShowTabDivider = prevState.effectiveShowTabDivider;


      var hasStateChanged = false;
      var newState = {};

      var nextTabs = createTabs(children);
      var nextActiveTabIndex = nextTabs.findIndex(function (_ref7) {
        var props = _ref7.props;
        return props.active;
      });
      if (nextActiveTabIndex >= 0 && nextActiveTabIndex !== prevActiveTabIndex && nextProps.defaultActiveTabIndex === undefined) {
        newState.activeTabIndex = nextActiveTabIndex;
        hasStateChanged = true;
      }

      // vertical tabs will only work when variant is "box"
      var nextEffectiveOrientation = variant === variants.BOX ? orientation : orientations.HORIZONTAL;
      if (nextEffectiveOrientation !== prevEffectiveOrientation) {
        newState.effectiveOrientation = nextEffectiveOrientation;
        hasStateChanged = true;
      }

      // align prop will not take effect when orientation is "vertical" or variant is "canvas"
      var nextEffectiveAlign = nextEffectiveOrientation === orientations.VERTICAL || variant === variants.CANVAS ? alignments.LEFT : align;
      if (nextEffectiveAlign !== prevEffectiveAlign) {
        newState.effectiveAlign = nextEffectiveAlign;
        hasStateChanged = true;
      }

      // tab divider will not show when orientation is "vertical" or variant is "underline"
      var nextEffectiveShowTabDivider = nextEffectiveOrientation === orientations.VERTICAL || variant === variants.UNDERLINE ? false : showTabDivider;
      if (nextEffectiveShowTabDivider !== prevEffectiveShowTabDivider) {
        newState.effectiveShowTabDivider = nextEffectiveShowTabDivider;
        hasStateChanged = true;
      }

      if (hasStateChanged) {
        return newState;
      }

      return null;
    }
  }]);

  return Tabs;
}(Component);

Tabs.propTypes = {
  /**
   * Control the active tab.
   * Overrides the deprecated active property on the Tab component.
   */
  activeTabIndex: PropTypes.number,
  /**
   * Specify how to justify the tabs within their container
   * When variant is set to "canvas", the effective alignment will always be "left"
   */
  align: PropTypes.oneOf(AVAILABLE_ALIGNMENTS),
  /**
   * Accepts Tab components
   */
  children: PropTypes.node,
  /**
   * Sets the initial active tab.
   * Overrides the deprecated active property on the Tab component.
   */
  defaultActiveTabIndex: PropTypes.number,
  /**
   * Called when user activates a tab
   */
  onTabChange: PropTypes.func,
  /**
   * Called when user closes a tab
   */
  onTabClose: PropTypes.func,
  /**
   * The list orientation of the tabs
   * Vertical tabs only works when variant is set to "box"
   */
  orientation: PropTypes.oneOf(AVAILABLE_ORIENTATIONS),
  /**
   * Show dividers between tabs
   * Only works in horizontal tabs and when variant is set to "box" or "canvas"
   */
  showTabDivider: PropTypes.bool,
  /**
   * Function to modify the component's styles
   */
  stylesheet: PropTypes.func,
  /**
   * The visual variant of the tabs
   */
  variant: PropTypes.oneOf(AVAILABLE_VARIANTS)
};
Tabs.defaultProps = {
  align: alignments.LEFT,
  onTabChange: function onTabChange() {},
  onTabClose: function onTabClose() {},
  variant: variants.UNDERLINE,
  orientation: orientations.HORIZONTAL,
  showTabDivider: true
};


var Tabs$1 = polyfill(Tabs);
Tabs.__docgenInfo = {
  "description": "",
  "displayName": "Tabs",
  "props": {
    "activeTabIndex": {
      "type": {
        "name": "number"
      },
      "required": false,
      "description": "Control the active tab.\nOverrides the deprecated active property on the Tab component."
    },
    "align": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_ALIGNMENTS"
      },
      "required": false,
      "description": "Specify how to justify the tabs within their container\nWhen variant is set to \"canvas\", the effective alignment will always be \"left\"",
      "defaultValue": {
        "value": "alignments.LEFT",
        "computed": true
      }
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Accepts Tab components"
    },
    "defaultActiveTabIndex": {
      "type": {
        "name": "number"
      },
      "required": false,
      "description": "Sets the initial active tab.\nOverrides the deprecated active property on the Tab component."
    },
    "onTabChange": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when user activates a tab",
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      }
    },
    "onTabClose": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when user closes a tab",
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      }
    },
    "orientation": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_ORIENTATIONS"
      },
      "required": false,
      "description": "The list orientation of the tabs\nVertical tabs only works when variant is set to \"box\"",
      "defaultValue": {
        "value": "orientations.HORIZONTAL",
        "computed": true
      }
    },
    "showTabDivider": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Show dividers between tabs\nOnly works in horizontal tabs and when variant is set to \"box\" or \"canvas\"",
      "defaultValue": {
        "value": "true",
        "computed": false
      }
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Function to modify the component's styles"
    },
    "variant": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_VARIANTS"
      },
      "required": false,
      "description": "The visual variant of the tabs",
      "defaultValue": {
        "value": "variants.UNDERLINE",
        "computed": true
      }
    }
  }
};

export default Tabs$1;
export { Tab, alignments, variants, orientations, AVAILABLE_ALIGNMENTS, AVAILABLE_VARIANTS, AVAILABLE_ORIENTATIONS };
